import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { AdminViewMenu } from "../../features/adminView/adminViewMenu/AdminViewMenu";
import { EmailLogsSection } from "../../features/adminView/EmailLogsSection";
import { AdminViewType } from "../../features/adminView/types/AdminViewType";
import { DownloadHistorySection } from "../../features/adminView/DownloadHistorySection";
import { LicenseLogSection } from "../../features/adminView/licenseLog/LicenseLogSection";
import { ProductMappingSection } from "../../features/adminView/ProductMappingSection";
import { ProjectTableSection } from "../../features/adminView/ProjectTableSection";
import { ExtendedProjectTableSection } from "../../features/adminView/ExtendedProjectTableSection";
import { CustomerTableSection } from "../../features/adminView/CustomerTableSection";
import { ReleasesSection } from "../../features/adminView/ReleasesSection";
import { ProjectAnalysisSection } from "../../features/adminView/ProjectAnalysisSection";
import { UpdateSyncSection } from "../../features/adminView/UpdateSyncSection";
import { ProtectedPage } from "../../components/pages/protectedPage/ProtectedPage";
import { Routes } from "../../routes/Routes";
import styles from "./AdminViewPage.module.scss";
import { useSession } from "../../hooks/useSession";
import {
  canAccessAdminView,
  canAccessAdminViewType,
  getDefaultAdminView,
} from "../../features/adminView/adminViewPermissions";

/**
 * Admin view page with menu-driven sections for license log and related data.
 */
export const AdminViewPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSession();
  const roles = session?.USER?.INT_AUTH_ROLE_IDS ?? [];
  const [selectedView, setSelectedView] = useState(() =>
    getDefaultAdminView(roles)
  );

  useEffect(() => {
    if (!canAccessAdminView(roles)) {
      navigate(Routes.homePage.toPath(), { replace: true });
    }
  }, [navigate, roles]);

  useEffect(() => {
    if (location.pathname.endsWith("/download-history")) {
      setSelectedView(AdminViewType.DOWNLOAD_HISTORY);
    } else if (location.pathname.endsWith("/project-table")) {
      setSelectedView(AdminViewType.PROJECT_TABLE);
    } else if (location.pathname.endsWith("/extended-project-table")) {
      setSelectedView(AdminViewType.EXTENDED_PROJECT_TABLE);
    } else if (location.pathname.endsWith("/product-mapping")) {
      setSelectedView(AdminViewType.PRODUCT_MAPPING);
    } else if (location.pathname.endsWith("/customer-table")) {
      setSelectedView(AdminViewType.CUSTOMER_TABLE);
    } else if (location.pathname.endsWith("/releases")) {
      setSelectedView(AdminViewType.RELEASES);
    } else if (location.pathname.endsWith("/project-analysis")) {
      setSelectedView(AdminViewType.PROJECT_ANALYSIS);
    } else if (location.pathname.endsWith("/update-sync")) {
      setSelectedView(AdminViewType.UPDATE_SYNC);
    } else if (location.pathname.endsWith("/email-logs")) {
      setSelectedView(AdminViewType.EMAIL_LOGS);
    } else {
      setSelectedView(getDefaultAdminView(roles));
    }
  }, [location.pathname, roles]);

  useEffect(() => {
    if (!canAccessAdminViewType(roles, selectedView)) {
      const defaultView = getDefaultAdminView(roles);
      setSelectedView(defaultView);
      navigate(`${Routes.adminView.toPath()}/${defaultView.toLowerCase().replaceAll("_", "-")}`, {
        replace: true,
      });
    }
  }, [navigate, roles, selectedView]);

  const handleSelect = (view: AdminViewType) => {
    setSelectedView(view);

    switch (view) {
      case AdminViewType.LICENSE_LOG:
        navigate(`${Routes.adminView.toPath()}/license-log`);
        break;
      case AdminViewType.DOWNLOAD_HISTORY:
        navigate(`${Routes.adminView.toPath()}/download-history`);
        break;
      case AdminViewType.PROJECT_TABLE:
        navigate(`${Routes.adminView.toPath()}/project-table`);
        break;
      case AdminViewType.EXTENDED_PROJECT_TABLE:
        navigate(`${Routes.adminView.toPath()}/extended-project-table`);
        break;
      case AdminViewType.PRODUCT_MAPPING:
        navigate(`${Routes.adminView.toPath()}/product-mapping`);
        break;
      case AdminViewType.CUSTOMER_TABLE:
        navigate(`${Routes.adminView.toPath()}/customer-table`);
        break;
      case AdminViewType.RELEASES:
        navigate(`${Routes.adminView.toPath()}/releases`);
        break;
      case AdminViewType.PROJECT_ANALYSIS:
        navigate(`${Routes.adminView.toPath()}/project-analysis`);
        break;
      case AdminViewType.UPDATE_SYNC:
        navigate(`${Routes.adminView.toPath()}/update-sync`);
        break;
      case AdminViewType.EMAIL_LOGS:
        navigate(Routes.emailLogs.toPath());
        break;
    }
  };

  const renderContent = () => {
    switch (selectedView) {
      case AdminViewType.LICENSE_LOG:
        return <LicenseLogSection />;
      case AdminViewType.DOWNLOAD_HISTORY:
        return <DownloadHistorySection />;
      case AdminViewType.PROJECT_TABLE:
        return <ProjectTableSection />;
      case AdminViewType.EXTENDED_PROJECT_TABLE:
        return <ExtendedProjectTableSection />;
      case AdminViewType.PRODUCT_MAPPING:
        return <ProductMappingSection />;
      case AdminViewType.CUSTOMER_TABLE:
        return <CustomerTableSection />;
      case AdminViewType.RELEASES:
        return <ReleasesSection />;
      case AdminViewType.PROJECT_ANALYSIS:
        return <ProjectAnalysisSection />;
      case AdminViewType.UPDATE_SYNC:
        return <UpdateSyncSection />;
      case AdminViewType.EMAIL_LOGS:
        return <EmailLogsSection />;
      default:
        return null;
    }
  };

  return (
    <ProtectedPage showBreadcrumbs iconName="user-circle-gear">
      <div
        className={`${styles.container} ${
          selectedView === AdminViewType.EMAIL_LOGS ? styles.emailLogsContainer : ""
        }`}
      >
        <AdminViewMenu
          roles={roles}
          selectedView={selectedView}
          onSelect={handleSelect}
        />
        <div className={styles.content}>{renderContent()}</div>
      </div>
    </ProtectedPage>
  );
};
